import Grid from "@mui/material/Grid";
import { useContext, useEffect, useState } from "react";
import { useController } from "react-hook-form";
import * as yup from "yup";
import RHFFinancialsField from "components/ui/atoms/formFields/RHFFinancialsField";

import { Box } from "@mui/material";
import CompanyFormHeader from "./CompanyFormHeader";
import useDebounce from "components/hooks/useDebounce";
import useTranslateDomain from "components/hooks/useTranslateDomain";
import { Form, FormContext } from "components/shared/Form";
import { buildGenericModelForm } from "components/shared/GenericModelForm";
import FooterFormDates from "components/ui/atoms/FooterFormDates";
import FormButtons from "components/ui/atoms/FormButtons";
import GooglePlacesAutoComplete from "components/ui/atoms/formFields/GooglePlacesAutoComplete";
import ListSelectField from "components/ui/atoms/formFields/ListSelectField";
import RHFDatePicker from "components/ui/atoms/formFields/RHFDatePicker";
import RHFTextField from "components/ui/atoms/formFields/RHFTextField";
import TagField from "components/ui/atoms/formFields/TagField";
import {
  useCompany,
  useCreateCompany,
  useDeleteCompany,
  useUpdateCompany,
} from "models/companies/apiCompany";
import { useLogHistory } from "models/history/apiHistory";
import { Stack } from "@mui/system";

const DEFAULT_VALIDATION_SCHEMA = {
  description: yup.string().nullable().required(),
  headquarter_address: yup.object().nullable(),
  founding_date: yup.date().nullable(),
  last_fundraising_date: yup.date().nullable(),
  employees_count: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  last_fundraising_amount: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  total_amount_raised: yup
    .number()
    .nullable(true)
    .transform((_, val) => (val ? Number(val) : null)), // Transform "" to null instead of Nan
  investors: yup.string().nullable(),
  sector_tags: yup.array().nullable(),
  business_model_tags: yup.array().nullable(),
  impact_model_tags: yup.array(),
  mapping_and_certification_tags: yup.array().nullable(),
  equity_story: yup.string().nullable(),
  management_data: yup.string().nullable(),
  impact: yup.string().nullable(),
  financial_data: yup.object().nullable(),
  link_drive: yup.string().url().nullable(),
};

const updateValidationSchema = yup.object({
  ...{
    main_domain: yup.string().nullable(),
    main_name: yup.string().nullable(),
    website: yup.string().url().nullable(),
  },
  ...DEFAULT_VALIDATION_SCHEMA,
});

const createValidationSchema = yup.object({
  ...{
    main_domain: yup.string().required(),
    main_name: yup.string().nullable(),
    website: yup.string().url().nullable(),
  },
  ...DEFAULT_VALIDATION_SCHEMA,
});

const getInitialValues = (object) => {
  return {
    main_domain: object?.best_domain || "",
    main_name: object?.best_name || "",
    website: object?.website || "",
    description: object?.description || "",
    headquarter_address: object?.headquarter_address || null,
    founding_date: object?.founding_date || null,
    last_fundraising_date: object?.last_fundraising_date || null,
    employees_count: object?.employees_count || "",
    last_fundraising_amount: object?.last_fundraising_amount || "",
    total_amount_raised: object?.total_amount_raised || "",
    investors: object?.investors || "",
    sector_tags: (object?.sector_tags || []).sort(),
    business_model_tags: (object?.business_model_tags || []).sort(),
    impact_model_tags: (object?.impact_model_tags || []).sort(),
    mapping_and_certification_tags: (
      object?.mapping_and_certification_tags || []
    ).sort(),
    business_model: object?.business_model || "",
    equity_story: object?.equity_story || "",
    management_data: object?.management_data || "",
    impact: object?.impact || "",
    financial_data: object?.financial_data,
    link_drive: object?.link_drive || "",
  };
};

const CompanyFormFields = () => {
  const {
    form: { control, watch },
    initialValues,
    onCancel,
    formType,
    object: company,
  } = useContext(FormContext);

  const [mainNameUpdatable, setMainNameUpdatable] = useState(
    !Boolean(initialValues?.best_name),
  );
  const { field: main_name_field } = useController({
    control: control,
    name: "main_name",
  });
  const main_domain = useDebounce(watch("main_domain"));
  const translateDomain = useTranslateDomain();

  useEffect(() => {
    if (!mainNameUpdatable) return;
    if (!main_domain) return;
    translateDomain({ company_domain: main_domain })
      .then((response) => {
        main_name_field.onChange(response.company_name);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [main_domain]);

  return (
    <>
      <CompanyFormHeader
        formType={formType}
        onClose={onCancel}
        company={company}
      />
      <Stack spacing={1} p={2} pt="70px">
        <ListSelectField
          options={company?.domains.map((x) => [x, x]) || []}
          name="main_domain"
          label="Principal company domain"
          freeSolo
        />
        <RHFTextField name="website" label="Website" fullWidth redirectButton />
        <RHFTextField
          name="main_name"
          label="Principal company name"
          onBlur={() => setMainNameUpdatable(false)}
          fullWidth
        />
        <RHFTextField name="link_drive" label="Link drive" fullWidth redirectButton />
        <TagField
          name="sector_tags"
          label="Sectors"
          tagTypeId="TAG_SECTOR"
          multiple
        />
        <TagField
          name="business_model_tags"
          label="Business model"
          tagTypeId="TAG_BUSINESS_MODEL"
          multiple
        />
        <TagField
          name="impact_model_tags"
          label="Company Impact"
          tagTypeId="TAG_IMPACT_MODEL"
          multiple
        />
        <TagField
          name="mapping_and_certification_tags"
          label="Mapping and certification"
          tagTypeId="TAG_MAPPING_AND_CERTIFICATIONS"
          multiple
        />
        <RHFTextField
          name="investors"
          label="Investors"
          multiline
          fullWidth
          maxRows={8}
        />
        <RHFTextField
          name="description"
          label="Description"
          multiline
          fullWidth
          maxRows={20}
        />
        <RHFTextField
          name="impact"
          label="Impact"
          multiline
          fullWidth
          maxRows={20}
        />
        <RHFTextField
          name="equity_story"
          label="Equity story"
          multiline
          fullWidth
          maxRows={20}
        />
        <RHFTextField
          name="management_data"
          label="Management data"
          multiline
          fullWidth
          maxRows={20}
        />
        <GooglePlacesAutoComplete
          name="headquarter_address"
          label="Headquarter address"
          fullWidth
        />
        <RHFTextField
          name="employees_count"
          label="Number of employees"
          type="number"
          fullWidth
        />
        <RHFDatePicker
          name="founding_date"
          label="Founded in"
          views={["year"]}
          openTo="year"
          format="YYYY"
          mask="____"
          fullWidth
        />
        <RHFDatePicker
          name="last_fundraising_date"
          label="Last fundraising date"
          views={["year", "month"]}
          openTo="year"
          format="MM/YYYY"
          mask="__/____"
          fullWidth
        />
        <RHFTextField
          name="last_fundraising_amount"
          label="Last fundraising amount"
          type="number"
          fullWidth
        />
        <RHFTextField
          name="total_amount_raised"
          label="Total raised amount"
          type="number"
          fullWidth
        />
        <Box maxWidth={"-webkit-fill-available"}>
          <RHFFinancialsField
            name="financial_data"
            label="Financials (in M.)"
          />
        </Box>
        <Box height={80} />
      </Stack>
      {company && <FooterFormDates object={company} />}

      <FormButtons />
    </>
  );
};

const CompanyForm = (props) => {
  const handleLog = useLogHistory("app.company");
  useEffect(() => {
    props.object?.id && handleLog(props.object.id);
  }, [props.object?.id]);

  return (
    <Form
      validationSchema={
        props.formType === "create"
          ? createValidationSchema
          : updateValidationSchema
      }
      prepareInitialValues={getInitialValues}
      {...props}
    >
      <CompanyFormFields />
    </Form>
  );
};

export const ModelCompanyForm = buildGenericModelForm({
  form: CompanyForm,
  useGet: useCompany,
  useCreate: useCreateCompany,
  useUpdate: useUpdateCompany,
  useDelete: useDeleteCompany,
});
